"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_NAVAMSA_CHART_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_NAVAMSA_CHART_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useRef, useState, type RefObject } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";

import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import {
  calculateNavamsa,
  type D9Planet,
  type NavamsaRequest,
  type NavamsaResponse,
} from "@/lib/api/navamsa";

type Language = "en" | "hi";

const COPY = {
  en: {
    heading: "Generate Your Navamsa Chart (D9)",
    submit: "Generate Navamsa chart",
    offerTitle: "Read your D9 in the full marriage context",
    offerBody:
      "The free result gives your engine-computed D9 placements. The Marriage Report adds the 7th house and lord, D1–D9 synthesis, Mangal Dosh, dasha timing, spouse indicators, and practical next steps.",
    offerCta: "Get Marriage Report",
    notesTitle: "Traditional D9 placement notes",
    notesBoundary:
      "These are placement-level interpretations, not a marriage prediction or compatibility verdict. Timing and relationship judgement require the D1 chart, 7th house, dashas, doshas, and partner context.",
  },
  hi: {
    heading: "Apni Navamsa Chart (D9) banayein",
    submit: "Navamsa chart banayein",
    offerTitle: "Apni D9 ko complete marriage context me samjhein",
    offerBody:
      "Free result engine-computed D9 placements deta hai. Marriage Report me 7th house aur lord, D1–D9 synthesis, Mangal Dosh, dasha timing, spouse indicators aur practical next steps milte hain.",
    offerCta: "Marriage Report paayein",
    notesTitle: "Traditional D9 placement notes",
    notesBoundary:
      "Yeh placement-level interpretations hain, marriage prediction ya compatibility verdict nahi. Timing aur relationship judgement ke liye D1, 7th house, dashas, doshas aur partner context chahiye.",
  },
} as const;

export default function NavamsaChartClient({ language = "en" }: { language?: Language }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<NavamsaResponse | null>(null);
  const [submittedBirth, setSubmittedBirth] = useState<NavamsaRequest | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const copy = COPY[language];

  async function handleSubmit(data: KundaliRequest) {
    setError(undefined);
    setLoading(true);
    setResult(null);
    try {
      const request = toNavamsaRequest(data);
      const response = await calculateNavamsa(request);
      setSubmittedBirth(request);
      setResult(response);
      trackCalculatorUsed({
        calculator: "navamsa-chart",
        language,
        source_path: sourcePath(language),
        has_time: true,
      });
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (submitError: unknown) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mb-10">
      <BirthDetailsForm
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        heading={copy.heading}
        headingLevel="h2"
        submitLabel={copy.submit}
        requireBirthTime
        requireResolvedPlace
        collectName={false}
        language={language}
        analyticsToolName="navamsa-chart"
        analyticsPlacement="navamsa_birth_details"
        rememberBirthDetails
        showTrustStrip
      />
      {result && submittedBirth ? (
        <NavamsaResults
          result={result}
          request={submittedBirth}
          resultRef={resultRef}
          language={language}
        />
      ) : null}
    </section>
  );
}

function toNavamsaRequest(data: KundaliRequest): NavamsaRequest {
  if (!data.birth_time) throw new Error("Exact birth time is required to calculate D9 houses.");
  if (data.latitude == null || data.longitude == null) {
    throw new Error("Select a birth city from the suggestions to calculate its coordinates.");
  }
  return {
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
  };
}

function NavamsaResults({
  result,
  request,
  resultRef,
  language,
}: {
  result: NavamsaResponse;
  request: NavamsaRequest;
  resultRef: RefObject<HTMLDivElement | null>;
  language: Language;
}) {
  const copy = COPY[language];
  return (
    <div
      ref={resultRef}
      className="mt-8 rounded-2xl border border-border-default bg-surface-raised p-6"
      data-design-owner="navamsa-result"
    >
      <NavamsaSummary result={result} />
      <D9Table planets={result.d9_chart} />
      <CalculatorReportBridge
        source={sourcePath(language)}
        placement={offerPlacement(language)}
        title={copy.offerTitle}
        body={copy.offerBody}
        cta={copy.offerCta}
        language={language}
        birthContext={{
          birthDate: request.birth_date,
          birthTime: request.birth_time,
          birthPlace: request.birth_place,
        }}
      />
      <div className="mt-8">
        <h4 className="mb-2 font-serif text-subtitle-small font-bold text-text-primary">
          {copy.notesTitle}
        </h4>
        <p className="mb-3 rounded-lg border border-warning/30 bg-warning/10 p-3 text-body-small leading-relaxed text-warning">
          {copy.notesBoundary}
        </p>
        <PredictionsList predictions={result.predictions} />
      </div>
      <p className="mt-6 text-caption text-text-muted">
        Generated by Ishvaram Jyotish engine · Sidereal zodiac (Lahiri ayanamsa) · Reference: BPHS
      </p>
    </div>
  );
}

function NavamsaSummary({ result }: { result: NavamsaResponse }) {
  return (
    <div className="mb-4">
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        Navamsa D9 · {result.birth_place} · {result.birth_date}
      </p>
      <h3 className="mt-1 font-serif text-title font-bold text-text-primary">
        Navamsa Lagna: <span className="text-intent-brand">{result.navamsa_lagna_display_en}</span>
        <span className="ml-2 text-body-small text-text-muted">
          {result.navamsa_lagna_display_hi}
        </span>
      </h3>
      {result.vargottama_count > 0 ? (
        <p className="mt-1 text-body-small text-text-muted">
          <strong className="text-text-primary">{result.vargottama_count}</strong> Vargottama planet
          {result.vargottama_count === 1 ? "" : "s"} — same rashi in D1 and D9.
        </p>
      ) : null}
    </div>
  );
}

function D9Table({ planets }: { planets: D9Planet[] }) {
  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full border-collapse text-body-small">
        <thead>
          <tr className="border-b border-border-default text-left text-caption font-semibold uppercase tracking-eyebrow text-intent-brand">
            <th className="py-2 pr-3">Planet</th>
            <th className="py-2 pr-3">D1 Rashi</th>
            <th className="py-2 pr-3">D9 Rashi</th>
            <th className="py-2 pr-3">D9 House</th>
            <th className="py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((planet) => (
            <tr
              key={planet.planet_id}
              className={`border-b border-border-subtle ${planet.is_vargottama ? "font-semibold" : ""}`}
            >
              <td className="py-2 pr-3 text-text-primary">
                {planet.planet_display_en}
                <span className="ml-1 text-caption text-text-muted">
                  {planet.planet_display_hi}
                </span>
              </td>
              <td className="py-2 pr-3 text-text-muted">{planet.d1_rashi_display_en}</td>
              <td className="py-2 pr-3 text-text-primary">{planet.d9_rashi_display_en}</td>
              <td className="py-2 pr-3 text-text-muted">{planet.d9_house}</td>
              <td className="py-2">{planet.is_vargottama ? <VargottamaBadge /> : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VargottamaBadge() {
  return (
    <span className="inline-block rounded bg-surface-brand-strong px-1.5 py-0.5 text-caption font-bold uppercase tracking-eyebrow text-text-on-brand">
      Vargottama
    </span>
  );
}

function PredictionsList({ predictions }: { predictions: string[] }) {
  return (
    <div className="space-y-2">
      {predictions.map((prediction) => (
        <p
          key={prediction}
          className="rounded-xl border border-border-default bg-surface-raised p-3 text-body-small text-text-primary"
        >
          {prediction}
        </p>
      ))}
    </div>
  );
}

function sourcePath(language: Language) {
  return language === "hi" ? "/hi/navamsa-chart" : "/navamsa-chart";
}

function offerPlacement(language: Language) {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_NAVAMSA_CHART_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_NAVAMSA_CHART_REPORT_BRIDGE;
}
